import { FC, memo, useCallback, useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import {
  useCompanyFilterActions,
  useCompanyFilterSlice,
  useCompanyFilterOptions,
} from "../../hooks";
import type { ItSpendOptions, SpendCategoryOption } from "./options";
import type { ItSpendRow } from "./slice";
import { SpendCategoryAutocomplete } from "@/components/filters/SpendCategory/SpendCategoryAutocomplete";

type SpendRowsFilterProps = {
  filterKey: "itSpend" | "aiSpend";
  categories: SpendCategoryOption[];
  description: string;
  categoryLabel?: string;
  categoryPlaceholder?: string;
  testIdPrefix: string;
};

export const SpendRowsFilter: FC<SpendRowsFilterProps> = ({
  filterKey,
  categories,
  description,
  categoryLabel,
  categoryPlaceholder,
  testIdPrefix,
}) => {
  const slice = useCompanyFilterSlice(filterKey);
  const { setSlice } = useCompanyFilterActions();

  const selectedCategoryIds = useMemo(
    () => slice.rows.map((r) => r.categoryId).filter(Boolean) as string[],
    [slice.rows]
  );

  const updateRow = useCallback(
    (index: number, patch: Partial<ItSpendRow>) => {
      const nextRows = slice.rows.map((row, i) =>
        i === index ? { ...row, ...patch } : row
      );
      setSlice(filterKey, { rows: nextRows });
    },
    [slice.rows, setSlice, filterKey]
  );

  const handleDeleteRow = useCallback(
    (index: number) => {
      const nextRows = slice.rows.filter((_, i) => i !== index);
      setSlice(filterKey, { rows: nextRows.length > 0 ? nextRows : [{}] });
    },
    [slice.rows, setSlice, filterKey]
  );

  const canAddRow = useMemo(
    () => categories.some((c) => !selectedCategoryIds.includes(c.id)),
    [categories, selectedCategoryIds]
  );

  const handleAddRow = useCallback(() => {
    setSlice(filterKey, { rows: [...slice.rows, {}] });
  }, [slice.rows, setSlice, filterKey]);

  const parseNumber = (raw: string): number | undefined => {
    if (raw.trim() === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>

      <Stack direction="column" spacing={2}>
        {slice.rows.map((row, index) => {
          const excludeIds = selectedCategoryIds.filter(
            (id) => id !== row.categoryId
          );

          return (
            <Stack key={index} direction="column" spacing={1.5}>
              {index > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pl: 0.5 }}
                >
                  AND
                </Typography>
              )}

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <SpendCategoryAutocomplete
                  categories={categories}
                  value={row.categoryId}
                  onChange={(id) => updateRow(index, { categoryId: id })}
                  excludeIds={excludeIds}
                  label={categoryLabel}
                  placeholder={categoryPlaceholder}
                  testId={`${testIdPrefix}-category-input-${index}`}
                />

                <IconButton
                  size="small"
                  onClick={() => handleDeleteRow(index)}
                  aria-label="Remove spend row"
                  data-testid={`${testIdPrefix}-remove-btn-${index}`}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label="Min (USD)"
                  type="number"
                  value={row.min ?? ""}
                  onChange={(e) =>
                    updateRow(index, { min: parseNumber(e.target.value) })
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        "data-testid": `${testIdPrefix}-min-input-${index}`,
                      },
                    },
                  }}
                  fullWidth
                />
                <TextField
                  size="small"
                  label="Max (USD)"
                  type="number"
                  value={row.max ?? ""}
                  onChange={(e) =>
                    updateRow(index, { max: parseNumber(e.target.value) })
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                      inputProps: {
                        min: 0,
                        "data-testid": `${testIdPrefix}-max-input-${index}`,
                      },
                    },
                  }}
                  fullWidth
                />
              </Stack>
            </Stack>
          );
        })}

        <Button
          size="small"
          startIcon={<AddIcon fontSize="small" />}
          onClick={handleAddRow}
          disabled={!canAddRow}
          data-testid={`${testIdPrefix}-add-btn`}
          sx={{ alignSelf: "flex-start", textTransform: "none" }}
        >
          Add category budget
        </Button>
      </Stack>
    </Box>
  );
};

export const ITSpendFilter: FC = memo(() => {
  const options = useCompanyFilterOptions("itSpend") as
    | ItSpendOptions
    | undefined;

  return (
    <SpendRowsFilter
      filterKey="itSpend"
      categories={options?.spendCategories ?? []}
      description="Add one or more IT spend budgets by category. Multiple rows are combined with AND."
      testIdPrefix="it-spend-filter"
    />
  );
});

ITSpendFilter.displayName = "ITSpendFilter";
